// Palette for the Command Deck layout on Home.
//
// This was once described as a "cool-neutral extension of the PP brand": slate
// grounds, a navy rail, and stage chips drawn from a spectrum of cyan, teal,
// violet, blue, orange and green. None of that is ours. The brandbook gives
// four colours — #e93529 red, #333333, #d8d8d8, #f4f4f4 — and one instruction
// about how to spend them (p.35): the layout leans on the greys, and red
// appears as an ACCENT to pick out what matters.
//
// The tokens below are the dark end of that ramp, which is where the app now
// lives. They are the same values components/common/brand.js uses, so Home and
// the deal screens are one surface rather than two that merely resemble each
// other. Semantic colours stay separate from the accent, because "on track"
// must never read as "brand red", and they are muted so they sit beside the
// neutrals instead of competing with them.
//
// The weekly presentation and the quarterly summary keep their own light
// palettes: those are documents meant for a projector and a PDF, and paper is
// still white.
//
// Kept in one place because the rail and every Home block draw from it.
export const BRIEF = {
  ground:  "#1c1c1c",   // page background
  card:    "#242424",
  border:  "#3a3a3a",   // card border
  divider: "#303030",   // hairline inside a card
  chip:    "#2c2c2c",   // quiet fill — pills, keycaps, inactive segments

  ink:     "#f4f4f4",   // headings, big numbers — the brand's light grey
  ink2:    "#b4b4b4",   // body copy
  muted:   "#8a8a8a",   // secondary labels
  faint:   "#7a7a7a",   // eyebrows, captions

  accent:     "#e93529", // PP red
  accentDark: "#c42d22",
  accentWash: "rgba(233,53,41,0.10)",
  accentLine: "rgba(233,53,41,0.32)",

  good:     "#4f9d76",
  goodWash: "rgba(79,157,118,0.12)",
  goodLine: "rgba(79,157,118,0.32)",
  warn:     "#c8922f",
  warnWash: "rgba(200,146,47,0.12)",
  warnLine: "rgba(200,146,47,0.32)",
  bad:      "#e93529",

  // Nav rail — a step deeper than the page so the two separate without a rule.
  rail:        "#161616",
  railDeep:    "#101010",
  railText:    "#8a8a8a",
  railTextOn:  "#f4f4f4",
  railGroup:   "#6f6f6f",
  railHover:   "rgba(244,244,244,0.06)",
  railActive:  "rgba(244,244,244,0.09)",
  railBorder:  "rgba(244,244,244,0.08)",
};

// Stage → chip colours, and deliberately not a spectrum.
//
// A stage is not a category to be told apart by hue; it is a position in a
// funnel. So the colour tracks how far along a deal is, and only the stages
// that mean something get one: gold while we are working on it, red when our
// name is on an offer, green when it is ours to lose. Everything else is grey
// — which is what the brandbook asks for, and also what makes the three that
// matter visible at a glance.
//
// Mirrors STAGE_TONE in components/common/brand.js and STAGE_COLORS in
// constants/dealStages.js. All three now agree.
export const BRIEF_STAGE_CHIP = {
  "Tracking":          { fg: "#8a8a8a", bg: "rgba(138,138,138,0.14)", bd: "rgba(138,138,138,0.25)" },
  "Sourcing":          { fg: "#8a8a8a", bg: "rgba(138,138,138,0.14)", bd: "rgba(138,138,138,0.25)" },
  "Outreach":          { fg: "#8a8a8a", bg: "rgba(138,138,138,0.14)", bd: "rgba(138,138,138,0.25)" },
  "Unsolicited Offer": { fg: "#8a8a8a", bg: "rgba(138,138,138,0.14)", bd: "rgba(138,138,138,0.25)" },
  "Screening":         { fg: "#b4b4b4", bg: "rgba(138,138,138,0.22)", bd: "rgba(138,138,138,0.35)" },
  "Underwriting":      { fg: "#c8922f", bg: "rgba(200,146,47,0.16)",  bd: "rgba(200,146,47,0.32)" },
  "LOI Submitted":     { fg: "#f04f44", bg: "rgba(233,53,41,0.16)",   bd: "rgba(233,53,41,0.34)" },
  "In Diligence":      { fg: "#7cbb99", bg: "rgba(79,157,118,0.16)",  bd: "rgba(79,157,118,0.32)" },
  "Acquired":          { fg: "#a9dcc0", bg: "rgba(79,157,118,0.30)",  bd: "rgba(79,157,118,0.50)" },
  "Passed":            { fg: "#8a8a8a", bg: "rgba(138,138,138,0.12)", bd: "rgba(138,138,138,0.22)" },
  "Lost":              { fg: "#e93529", bg: "rgba(233,53,41,0.10)",   bd: "rgba(233,53,41,0.22)" },
  "Dead":              { fg: "#7a7a7a", bg: "#242424",                bd: "rgba(106,106,106,0.22)" },
};
